import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Flame, Search, Loader2, Salad, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { RECIPE_FILTERS, matchesRecipeFilter } from "@/lib/recipes";

export default function Recipes() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState(RECIPE_FILTERS[0]);

  useEffect(() => {
    api.get("/recipes").then((r) => setRecipes(r.data.filter((x) => x.active !== false))).catch(() => setRecipes([]));
  }, []);

  const visible = useMemo(() => {
    if (!recipes) return [];
    const q = query.trim().toLowerCase();
    return recipes.filter((r) => matchesRecipeFilter(r, filter) && (!q || r.name.toLowerCase().includes(q) || r.category.toLowerCase().includes(q) || (r.tags || []).some((t) => t.toLowerCase().includes(q))));
  }, [recipes, query, filter]);

  return (
    <div className="animate-fade-in pb-4">
      <div className="px-5 pt-4 pb-3 flex items-end justify-between">
        <div>
          <span className="text-[11px] font-semibold tracking-wider uppercase text-emerald-600 dark:text-emerald-400">Eat Well</span>
          <h1 className="text-2xl font-extrabold text-emerald-950 dark:text-emerald-50 font-head">Healthy Recipes</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Dietitian-approved meals for every goal</p>
        </div>
        <span data-testid="recipe-count" className="text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-800 px-2.5 py-1 rounded-full mb-1">
          {recipes ? `${visible.length}/${recipes.length}` : "…"}
        </span>
      </div>

      <div className="px-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid="recipe-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search recipes…"
            className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-200 dark:border-emerald-800 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
          />
        </div>
      </div>

      <div className="mt-3 flex gap-2 overflow-x-auto scrollbar-none px-4 pb-1">
        {RECIPE_FILTERS.map((f) => (
          <button
            key={f.label}
            data-testid={`recipe-filter-${f.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}
            onClick={() => setFilter(f)}
            className={`shrink-0 text-xs font-semibold px-3.5 py-2 rounded-full border transition-colors ${
              filter.label === f.label
                ? "bg-emerald-700 dark:bg-emerald-500 text-white dark:text-emerald-950 border-emerald-700 dark:border-emerald-500"
                : "bg-white dark:bg-emerald-900/60 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-emerald-800"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {!recipes && <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>}

      {recipes && visible.length === 0 && (
        <div className="px-6 py-14 text-center" data-testid="recipe-empty-state">
          <Salad className="w-10 h-10 mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">No recipes match your search.</p>
        </div>
      )}

      <div className="px-4 mt-3 grid grid-cols-2 gap-3">
        {visible.map((r) => (
          <div
            key={r.id}
            data-testid={`recipe-card-${r.id}`}
            className="text-left bg-white dark:bg-emerald-900/60 rounded-2xl border border-slate-100 dark:border-emerald-800 overflow-hidden flex flex-col"
          >
            <div className="relative h-28 bg-slate-100 dark:bg-emerald-800 overflow-hidden">
              <img src={r.image} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
              <span className="absolute top-2 left-2 text-[10px] font-bold bg-white/90 dark:bg-emerald-950/90 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full">{r.category}</span>
            </div>
            <div className="p-3 flex flex-col flex-1">
              <p className="text-sm font-semibold text-emerald-950 dark:text-emerald-50 line-clamp-2 leading-snug">{r.name}</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 line-clamp-2 leading-snug flex-1">{r.intro}</p>
              <div className="flex items-center gap-2.5 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{r.total_time}</span>
                <span className="flex items-center gap-1"><Flame className="w-3 h-3" />{r.nutrition?.Calories}</span>
              </div>
              <button
                data-testid={`recipe-view-${r.id}`}
                onClick={() => navigate(`/recipes/${r.id}`)}
                className="mt-2.5 w-full flex items-center justify-center gap-1 bg-emerald-50 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300 text-xs font-bold py-2 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-700 transition"
              >
                View Recipe <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
